import * as React from "react";
import { api, type ApiOrg, type ApiUser, type RegistrantsResponse, type Registrant, type SavedView } from "../api.js";
import { activeFilterCount, applyFilters, applySort, type FilterMap, type SortSpec } from "../filter.js";
import { DataTable } from "../components/DataTable.js";
import { FilterSidebar } from "../components/FilterSidebar.js";
import { BarChart, Donut, LineAreaChart } from "../components/Charts.js";
import { RegistrantDrawer } from "../components/RegistrantDrawer.js";
import { SaveViewModal } from "../components/SaveViewModal.js";
import { ConfirmModal } from "../components/ConfirmModal.js";
import { ExportModal } from "../components/ExportModal.js";
import { Icon } from "../components/Icon.js";

type Props = {
  user: ApiUser;
  org: ApiOrg | null;
  instance: string;
};

type Tab = "table" | "charts";

// The event picker at the top of the sidebar writes into filters.eventName
// (kind: "set", values: eventName[]). We treat that single key as sticky:
// it lives OUTSIDE the saved-view lifecycle (applyView preserves it,
// save/update strips it, activeViewDirty ignores it) and mirrors to
// localStorage so a page reload doesn't clear it either.
const STICKY_EVENTS_KEY = "tidyview.eventNames";

function readStickyEventNames(): string[] {
  try {
    const raw = localStorage.getItem(STICKY_EVENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((s): s is string => typeof s === "string") : [];
  } catch {
    return [];
  }
}

function writeStickyEventNames(names: string[]): void {
  try {
    if (names.length) localStorage.setItem(STICKY_EVENTS_KEY, JSON.stringify(names));
    else localStorage.removeItem(STICKY_EVENTS_KEY);
  } catch { /* storage unavailable (private browsing, etc.) */ }
}

function stripEventName(filters: FilterMap): FilterMap {
  if (!filters.eventName) return filters;
  const { eventName: _dropped, ...rest } = filters;
  return rest;
}

export function Dashboard({ user, org, instance }: Props) {
  const [data, setData] = React.useState<RegistrantsResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const [tab, setTab] = React.useState<Tab>("table");
  const [filters, setFilters] = React.useState<FilterMap>({});
  const [search, setSearch] = React.useState("");
  const [visibleIds, setVisibleIds] = React.useState<string[]>([]);
  const [columnOrder, setColumnOrder] = React.useState<string[]>([]);
  const [sort, setSort] = React.useState<SortSpec | null>(null);
  const [showSaveView, setShowSaveView] = React.useState(false);
  const [confirmUpdateView, setConfirmUpdateView] = React.useState(false);
  const [selectedRow, setSelectedRow] = React.useState<Registrant | null>(null);
  const [views, setViews] = React.useState<SavedView[]>([]);
  const [activeViewId, setActiveViewId] = React.useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [userMenuOpen, setUserMenuOpen] = React.useState(false);
  const [showExportModal, setShowExportModal] = React.useState(false);

  // Guards the persistence effect below from firing before hydration has
  // completed — otherwise the initial empty-filters render would clear
  // localStorage before we get a chance to read it.
  const stickyHydrated = React.useRef(false);

  React.useEffect(() => {
    let alive = true;
    api.registrants().then((r) => {
      if (!alive) return;
      setData(r);
      setVisibleIds(r.defaultVisibleColumnIds);
      setColumnOrder(r.columns.map((c) => c.id));
      // Hydrate the sticky event selection from localStorage, dropping any
      // names that no longer match a live event (guards against org
      // switches and deleted events).
      const stored = readStickyEventNames();
      if (stored.length) {
        const liveNames = new Set(r.events.map((e) => e.name));
        const kept = stored.filter((n) => liveNames.has(n));
        if (kept.length) {
          setFilters((f) => ({ ...f, eventName: { kind: "set", values: kept } }));
        } else {
          writeStickyEventNames([]);
        }
      }
      stickyHydrated.current = true;
    }).catch((e) => alive && setError(String(e)));
    api.views().then((v) => alive && setViews(v)).catch(() => { /* non-fatal */ });
    return () => { alive = false; };
  }, []);

  React.useEffect(() => {
    if (!userMenuOpen) return;
    const handler = () => setUserMenuOpen(false);
    window.addEventListener("click", handler);
    return () => window.removeEventListener("click", handler);
  }, [userMenuOpen]);

  // Mirror the current event selection to localStorage on every change,
  // so page reloads keep the same events picked. Skips runs before
  // hydration so the initial empty-filters render can't stomp on the
  // stored value.
  const eventFilter = filters.eventName;
  React.useEffect(() => {
    if (!stickyHydrated.current) return;
    const values = eventFilter?.kind === "set" ? eventFilter.values : [];
    writeStickyEventNames(values);
  }, [eventFilter]);

  const filtersWithSearch: FilterMap = React.useMemo(() => {
    if (!search.trim()) return filters;
    return { ...filters, fullName: { kind: "text", q: search.trim() } };
  }, [filters, search]);

  const filteredRows = React.useMemo(() => {
    if (!data) return [];
    return applySort(applyFilters(data.registrants, data.columns, filtersWithSearch), data.columns, sort);
  }, [data, filtersWithSearch, sort]);

  React.useEffect(() => {
    const visible = new Set(filteredRows.map((r) => r.id));
    setSelected((prev) => {
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (visible.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [filteredRows]);

  const filterSummary = React.useMemo(() => {
    if (!data) return "No filters";
    const parts: string[] = [];
    // Skip eventName here — it's the sticky selection, not part of the
    // saved view's summary.
    for (const [colId, f] of Object.entries(filters)) {
      if (!f || colId === "eventName") continue;
      const col = data.columns.find((c) => c.id === colId);
      if (!col) continue;
      switch (f.kind) {
        case "set": if (f.values.length) parts.push(`${col.label}: ${f.values.join(", ")}`); break;
        case "bool": parts.push(`${col.label}: ${f.value ? "Yes" : "No"}`); break;
        case "range":
          if (f.min !== undefined || f.max !== undefined) {
            parts.push(`${col.label}: ${f.min ?? "—"}..${f.max ?? "—"}`);
          }
          break;
        case "dateRange":
          if (f.from || f.to) parts.push(`${col.label}: ${f.from ?? ""}..${f.to ?? ""}`);
          break;
        case "text": if (f.q) parts.push(`${col.label}: "${f.q}"`); break;
      }
    }
    if (search.trim()) parts.unshift(`search "${search.trim()}"`);
    return parts.join(" · ") || "No filters";
  }, [filters, search, data]);

  const activeView = React.useMemo(
    () => (activeViewId ? views.find((v) => v.id === activeViewId) ?? null : null),
    [activeViewId, views],
  );

  const activeViewDirty = React.useMemo(() => {
    if (!activeView) return false;
    const savedOrder = activeView.columnOrder ?? synthesiseColumnOrder(activeView.visibleColumnIds, data?.columns ?? []);
    // Event selection is sticky (see stripEventName above) — it lives
    // outside saved views, so picking a different event shouldn't flag a
    // view as dirty.
    return !deepEqual(stripEventName(activeView.filters as FilterMap), stripEventName(filters))
      || !deepEqual(activeView.visibleColumnIds, visibleIds)
      || !deepEqual(savedOrder, columnOrder)
      || !deepEqual(activeView.sort, sort);
  }, [activeView, filters, visibleIds, columnOrder, sort, data]);

  const displayOrder = React.useMemo(() => {
    const visibleSet = new Set(visibleIds);
    return columnOrder.filter((id) => visibleSet.has(id));
  }, [columnOrder, visibleIds]);

  if (error === "Error: unauthenticated") {
    window.location.href = "/auth/login";
    return null;
  }

  if (error) return <div className="card" style={{ margin: 24, padding: 20 }}><b>Error:</b> {error}</div>;
  if (!data) return <div className="card" style={{ margin: 24, padding: 20 }}>Loading…</div>;

  async function saveCurrentView(name: string) {
    // Sticky event selection is not part of a saved view — see the
    // stripEventName / persistence machinery above.
    const view = await api.saveView({
      name,
      filters: stripEventName(filters) as Record<string, unknown>,
      visibleColumnIds: visibleIds,
      columnOrder,
      sort,
    });
    setViews((v) => [view, ...v]);
    setActiveViewId(view.id);
    setShowSaveView(false);
  }

  function applyView(v: SavedView) {
    // Preserve the current event selection when switching views. The
    // team's biggest gripe with the previous behaviour was that changing
    // views wiped their event pick.
    const withStickyEvent = { ...stripEventName(v.filters as FilterMap) };
    if (filters.eventName) withStickyEvent.eventName = filters.eventName;
    setFilters(withStickyEvent);
    if (Array.isArray(v.visibleColumnIds) && v.visibleColumnIds.length > 0) {
      setVisibleIds(v.visibleColumnIds);
    }
    if (v.columnOrder && v.columnOrder.length > 0) {
      setColumnOrder(v.columnOrder);
    } else if (data) {
      setColumnOrder(synthesiseColumnOrder(v.visibleColumnIds, data.columns));
    }
    setSort(v.sort);
    setActiveViewId(v.id);
  }

  async function deleteView(id: string) {
    await api.deleteView(id);
    setViews((v) => v.filter((x) => x.id !== id));
    if (activeViewId === id) setActiveViewId(null);
  }

  async function renameView(id: string, name: string) {
    const existing = views.find((v) => v.id === id);
    if (!existing) return;
    const updated = await api.updateView(id, {
      name,
      filters: existing.filters,
      visibleColumnIds: existing.visibleColumnIds,
      columnOrder: existing.columnOrder,
      sort: existing.sort,
    });
    setViews((vs) => vs.map((v) => (v.id === id ? updated : v)));
  }

  async function updateActiveView() {
    if (!activeViewId) return;
    const existing = views.find((v) => v.id === activeViewId);
    if (!existing) return;
    const updated = await api.updateView(activeViewId, {
      name: existing.name,
      filters: stripEventName(filters) as Record<string, unknown>,
      visibleColumnIds: visibleIds,
      columnOrder,
      sort,
    });
    setViews((vs) => vs.map((v) => (v.id === activeViewId ? updated : v)));
    setConfirmUpdateView(false);
  }

  function exportHref(format: "csv" | "pdf"): string {
    const params = new URLSearchParams();
    if (displayOrder.length) params.set("cols", displayOrder.join(","));
    if (Object.keys(filtersWithSearch).length) params.set("filters", JSON.stringify(filtersWithSearch));
    if (sort) params.set("sort", JSON.stringify(sort));
    // The view name titles the PDF's header block as well as the filename;
    // flag unsaved tweaks so an export can't misrepresent itself as the
    // saved view.
    if (activeView) params.set("name", activeView.name);
    if (activeView && activeViewDirty) params.set("modified", "1");
    return `/api/export.${format}?${params.toString()}`;
  }

  const filterCount = activeFilterCount(filters);

  const userInitials = (() => {
    const name = user.displayName ?? user.email ?? "?";
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
    if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
    return "?";
  })();
  const orgInitial = (org?.name ?? "?")[0]!.toUpperCase();

  return (
    <div className="dash-root">
      <header className="topbar">
        <div className="tb-l">
          <div className="brand-mark">
            <span className="dot" />Tidy<b>View</b>
          </div>
          <div className="org-pill" title={org?.name ?? ""}>
            <div className="avatar" style={{ background: "#1a8fbf", width: 22, height: 22, fontSize: 10 }}>{orgInitial}</div>
            <span>{org?.name ?? "—"}</span>
          </div>
        </div>

        <div className="tb-search">
          <Icon name="search" size={14} />
          <input
            type="text"
            placeholder="Search by name, email, ticket…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="icon-btn" onClick={() => setSearch("")} style={{ position: "absolute", right: 8, top: 5 }} aria-label="Clear search">
              <Icon name="x" size={12} />
            </button>
          )}
        </div>

        <div className="tb-r">
          <button className="btn btn-primary btn-sm" onClick={() => setShowExportModal(true)}>
            <Icon name="download" size={13} /> Export
          </button>
          <button className="icon-btn-lg" title="Refresh" onClick={() => window.location.reload()}>
            <Icon name="refresh" size={16} />
          </button>
          <div className="user-menu-wrap" onClick={(e) => { e.stopPropagation(); setUserMenuOpen((o) => !o); }}>
            <div className="avatar" style={{ background: "#1a8fbf" }}>{userInitials}</div>
            {userMenuOpen && (
              <div className="user-menu card fade-in" onClick={(e) => e.stopPropagation()}>
                <div className="um-head">
                  <div className="avatar" style={{ background: "#1a8fbf", width: 36, height: 36, fontSize: 13 }}>{userInitials}</div>
                  <div>
                    <div className="um-name">{user.displayName ?? "—"}</div>
                    <div className="um-email">{user.email ?? ""}</div>
                  </div>
                </div>
                <div className="divider" />
                <a href="#/overrides" className="um-item" style={{ display: "flex", alignItems: "center", gap: 8, textDecoration: "none" }}>
                  <Icon name="upload" size={14} /> Attendee overrides
                </a>
                <a href="#/webhooks" className="um-item" style={{ display: "flex", alignItems: "center", gap: 8, textDecoration: "none" }}>
                  <Icon name="sparkles" size={14} /> Webhooks
                </a>
                <form method="POST" action="/auth/switch" style={{ display: "block" }}>
                  <button type="submit" className="um-item">
                    <Icon name="building" size={14} /> Switch organisation
                  </button>
                </form>
                <button className="um-item danger" onClick={async () => { await api.logout(); window.location.href = "/"; }}>
                  <Icon name="logout" size={14} /> Sign out
                </button>
                <div className="divider" />
                <div className="um-instance">{instance}</div>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="dash-body">
        <FilterSidebar
          rows={data.registrants}
          columns={data.columns}
          events={data.events}
          filters={filters}
          onChange={(f) => { setFilters(f); setActiveViewId(null); }}
          onClearAll={() => {
            // Sticky event is preserved across a "clear all" — someone
            // filtering "just BTC 2026" doesn't expect that pick to also
            // vanish when they reset the other filters.
            setFilters((f) => {
              const next: FilterMap = {};
              if (f.eventName) next.eventName = f.eventName;
              return next;
            });
            setSearch("");
            setActiveViewId(null);
          }}
          savedViews={views}
          activeViewId={activeViewId}
          activeViewDirty={activeViewDirty}
          onPickView={applyView}
          onSaveView={() => setShowSaveView(true)}
          onRenameView={renameView}
          onUpdateActiveView={() => setConfirmUpdateView(true)}
          onDeleteView={deleteView}
          collapsed={sidebarCollapsed}
          onToggleCollapsed={() => setSidebarCollapsed((s) => !s)}
          visibleIds={visibleIds}
          defaultVisibleIds={data.defaultVisibleColumnIds}
          onColumnsChange={setVisibleIds}
          columnOrder={columnOrder}
          onColumnOrderChange={setColumnOrder}
        />

        <main className="dash-main">
          <div className="dash-tabs">
            <button className={"tab" + (tab === "table" ? " active" : "")} onClick={() => setTab("table")}>
              <Icon name="table" size={14} /> Table
              <span className="tab-count">{filteredRows.length}</span>
            </button>
            <button className={"tab" + (tab === "charts" ? " active" : "")} onClick={() => setTab("charts")}>
              <Icon name="chart" size={14} /> Charts
            </button>
            <div className="tab-spacer" />
            <div className="dash-tip">
              <Icon name="sparkles" size={13} /> Filters apply to both tabs
            </div>
          </div>

          {filterCount > 0 && (
            <div className="active-filters">
              <span className="af-label">Filtering:</span>
              {Object.entries(filters).map(([colId, f]) => {
                const col = data.columns.find((c) => c.id === colId);
                if (!col || !f) return null;
                const clear = () => {
                  const next = { ...filters };
                  delete next[colId];
                  setFilters(next);
                  setActiveViewId(null);
                };
                if (f.kind === "set" && f.values.length) {
                  return f.values.map((v) => (
                    <FilterChip key={`${colId}:${v}`} onClear={() => {
                      const remaining = f.values.filter((x) => x !== v);
                      const next = { ...filters };
                      if (remaining.length === 0) delete next[colId];
                      else next[colId] = { kind: "set", values: remaining };
                      setFilters(next);
                      setActiveViewId(null);
                    }}>
                      {col.label}: {v}
                    </FilterChip>
                  ));
                }
                if (f.kind === "bool") {
                  return <FilterChip key={colId} onClear={clear}>{col.label}: {f.value ? "Yes" : "No"}</FilterChip>;
                }
                if (f.kind === "range" && (f.min !== undefined || f.max !== undefined)) {
                  return <FilterChip key={colId} onClear={clear}>{col.label}: {f.min ?? "—"}..{f.max ?? "—"}</FilterChip>;
                }
                if (f.kind === "dateRange" && (f.from || f.to)) {
                  return <FilterChip key={colId} onClear={clear}>{col.label}: {f.from ?? ""}..{f.to ?? ""}</FilterChip>;
                }
                if (f.kind === "text" && f.q) {
                  return <FilterChip key={colId} onClear={clear}>{col.label}: "{f.q}"</FilterChip>;
                }
                return null;
              })}
              <button className="link-btn" onClick={() => {
                setFilters((f) => {
              const next: FilterMap = {};
              if (f.eventName) next.eventName = f.eventName;
              return next;
            });
                setActiveViewId(null);
              }}>Clear all</button>
            </div>
          )}

          {tab === "table" && (
            <div className="table-toolbar">
              <div className="tt-left">
                {selected.size > 0 ? (
                  <div className="bulk-bar">
                    <span className="bulk-count">{selected.size} selected</span>
                    <button className="btn btn-sm btn-ghost" onClick={() => setSelected(new Set())}>Clear</button>
                  </div>
                ) : (
                  <div className="tt-counts">
                    <span className="tt-num">{filteredRows.length}</span>
                    <span className="tt-of">of {data.registrants.length} registrants</span>
                  </div>
                )}
              </div>
              <div className="tt-right">
                {sort && (
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => setSort({ ...sort, dir: sort.dir === "asc" ? "desc" : "asc" })}
                  >
                    <Icon name={sort.dir === "asc" ? "arrowUp" : "arrowDown"} size={13} />
                    Sort: {data.columns.find((c) => c.id === sort.columnId)?.label ?? sort.columnId}
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="dash-content">
            {tab === "table" && (
              <DataTable
                rows={filteredRows}
                columns={data.columns}
                visibleIds={displayOrder}
                sort={sort}
                onSort={setSort}
                onRowClick={setSelectedRow}
                selected={selected}
                setSelected={setSelected}
              />
            )}
            {tab === "charts" && (
              <div className="charts-grid fade-in">
                <LineAreaChart
                  title="Sign-ups over time"
                  sub={`${filteredRows.length} total`}
                  perPeriod={data.charts.signupsByWeek}
                  cumulative={data.charts.signupsCumulative}
                />
                <Donut title="Gender split" sub="Live from filters" data={data.charts.genderDonut} />
                <BarChart title="Age distribution" data={data.charts.ageHistogram} />
              </div>
            )}
          </div>
        </main>
      </div>

      {selectedRow && (
        <RegistrantDrawer
          row={selectedRow}
          columns={data.columns}
          onClose={() => setSelectedRow(null)}
        />
      )}
      {showSaveView && (
        <SaveViewModal
          onSave={saveCurrentView}
          onClose={() => setShowSaveView(false)}
          filterSummary={filterSummary}
        />
      )}
      {showExportModal && (
        <ExportModal
          hrefFor={exportHref}
          onClose={() => setShowExportModal(false)}
        />
      )}
      {confirmUpdateView && activeView && (
        <ConfirmModal
          title={`Update "${activeView.name}"?`}
          confirmLabel="Update view"
          body={
            <>
              This will replace the saved filters, columns, and sort for
              <b> "{activeView.name}"</b> with what you have selected now. The view's name won't change.
            </>
          }
          onConfirm={updateActiveView}
          onClose={() => setConfirmUpdateView(false)}
        />
      )}

      <style>{`
        .dash-root {
          position: fixed; inset: 0;
          display: flex; flex-direction: column;
          background: var(--bg);
        }
        .dash-body { flex: 1; display: flex; min-height: 0; overflow: hidden; }
        .dash-main { flex: 1; display: flex; flex-direction: column; min-width: 0; min-height: 0; padding: 16px 20px 0; gap: 0; overflow: hidden; }

        .topbar {
          height: 60px;
          display: grid;
          grid-template-columns: auto 1fr auto;
          align-items: center; gap: 16px;
          padding: 0 20px;
          background: white;
          border-bottom: 1px solid var(--border);
          position: relative;
          z-index: 10;
        }
        .tb-l { display: flex; align-items: center; gap: 14px; }
        .brand-mark {
          font-weight: 800; font-size: 16px; letter-spacing: -0.02em; color: var(--ink-900);
          display: flex; align-items: center; gap: 8px;
        }
        .brand-mark .dot { width: 22px; height: 22px; border-radius: 7px; background: var(--brand-500); position: relative; display: inline-block; }
        .brand-mark .dot::after { content: ""; position: absolute; right: -2px; top: -2px; width: 8px; height: 8px; border-radius: 50%; background: #ffd96b; }
        .brand-mark b { color: var(--brand-600); font-weight: 800; }

        .org-pill {
          display: flex; align-items: center; gap: 8px;
          padding: 4px 10px 4px 4px;
          background: var(--ink-50);
          border: 1px solid var(--border);
          border-radius: var(--r-full);
          font-size: 13px; font-weight: 600; color: var(--ink-800);
          max-width: 240px;
        }
        .org-pill span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

        .tb-search { position: relative; max-width: 520px; width: 100%; justify-self: center; }
        .tb-search > svg { position: absolute; left: 12px; top: 11px; color: var(--ink-400); pointer-events: none; }
        .tb-search input {
          width: 100%; height: 36px;
          padding: 0 36px 0 36px;
          border-radius: var(--r-md);
          border: 1px solid var(--border);
          background: var(--ink-50);
          font-size: 13.5px;
          outline: none;
          transition: all 120ms;
        }
        .tb-search input:focus { background: white; border-color: var(--brand-400); box-shadow: var(--shadow-focus); }
        .tb-search input::placeholder { color: var(--ink-400); }

        .tb-r { display: flex; align-items: center; gap: 8px; }
        .icon-btn-lg { width: 36px; height: 36px; border-radius: var(--r-md); display: grid; place-content: center; color: var(--ink-500); border: 0; background: transparent; cursor: pointer; }
        .icon-btn-lg:hover { background: var(--ink-100); color: var(--ink-800); }
        .user-menu-wrap { position: relative; cursor: pointer; padding: 4px; border-radius: var(--r-full); }
        .user-menu-wrap:hover { background: var(--ink-100); }
        .user-menu { position: absolute; top: calc(100% + 6px); right: 0; width: 240px; padding: 6px; box-shadow: var(--shadow-lg); z-index: 50; background: var(--surface); }
        .um-head { display: flex; gap: 10px; padding: 10px 10px 12px; align-items: center; }
        .um-name { font-size: 13.5px; font-weight: 700; color: var(--ink-900); }
        .um-email { font-size: 12px; color: var(--ink-500); overflow: hidden; text-overflow: ellipsis; max-width: 160px; }
        .um-item { display: flex; align-items: center; gap: 10px; width: 100%; padding: 8px 10px; border-radius: 6px; font-size: 13px; color: var(--ink-700); text-align: left; border: 0; background: transparent; cursor: pointer; }
        .um-item:hover { background: var(--ink-100); color: var(--ink-900); }
        .um-item.danger { color: var(--rose-500); }
        .um-item.danger:hover { background: var(--red-50); }
        .um-instance { padding: 6px 10px; font-size: 11px; color: var(--ink-400); text-transform: uppercase; letter-spacing: 0.06em; }

        .dash-tabs {
          display: flex; align-items: center; gap: 4px;
          margin-bottom: 14px;
          border-bottom: 1px solid var(--border);
        }
        .tab {
          height: 38px; padding: 0 14px;
          display: inline-flex; align-items: center; gap: 8px;
          font-weight: 600; font-size: 13.5px; color: var(--ink-500);
          border: 0; background: transparent;
          border-bottom: 2px solid transparent;
          margin-bottom: -1px;
          cursor: pointer;
          transition: color 120ms;
        }
        .tab:hover { color: var(--ink-800); }
        .tab.active { color: var(--brand-600); border-bottom-color: var(--brand-500); }
        .tab-count { background: var(--ink-100); color: var(--ink-600); padding: 1px 7px; border-radius: var(--r-full); font-size: 11px; font-weight: 700; font-variant-numeric: tabular-nums; }
        .tab.active .tab-count { background: var(--brand-50); color: var(--brand-700); }
        .tab-spacer { flex: 1; }
        .dash-tip { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: var(--ink-500); padding-right: 8px; }
        .dash-tip svg { color: var(--brand-400); }

        .active-filters {
          display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
          margin-bottom: 14px; padding: 8px 12px;
          background: var(--brand-50);
          border-radius: var(--r-md);
        }
        .af-label { font-size: 12px; font-weight: 700; color: var(--brand-700); padding: 0 4px; }

        .table-toolbar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; gap: 12px; }
        .tt-counts { display: flex; align-items: baseline; gap: 6px; }
        .tt-num { font-size: 18px; font-weight: 700; color: var(--ink-900); font-variant-numeric: tabular-nums; }
        .tt-of { font-size: 13px; color: var(--ink-500); }
        .tt-right { display: flex; gap: 8px; align-items: center; }

        .bulk-bar { display: flex; align-items: center; gap: 8px; padding: 4px 8px 4px 12px; background: var(--brand-50); border: 1px solid var(--brand-200); border-radius: var(--r-md); }
        .bulk-count { font-size: 13px; font-weight: 700; color: var(--brand-700); margin-right: 4px; }

        .dash-content { flex: 1; min-height: 0; padding-bottom: 16px; display: flex; flex-direction: column; overflow: auto; }
        .charts-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
          gap: 14px;
        }

        @media (max-width: 920px) {
          .dash-main { padding: 12px 12px 0; }
          .dash-tip { display: none; }
        }
        @media (max-width: 760px) {
          .topbar { grid-template-columns: auto 1fr auto; }
          .tb-l .brand-mark { display: none; }
          .org-pill span { display: none; }
        }
      `}</style>
    </div>
  );
}

function FilterChip({ children, onClear }: { children: React.ReactNode; onClear: () => void }) {
  return (
    <span className="filter-chip">
      {children}
      <button onClick={onClear} aria-label="Remove filter"><Icon name="x" size={11} /></button>
      <style>{`
        .filter-chip {
          display: inline-flex; align-items: center; gap: 6px;
          height: 24px; padding: 0 4px 0 10px;
          background: white; border: 1px solid var(--brand-200);
          border-radius: var(--r-full);
          font-size: 12px; color: var(--brand-800); font-weight: 600;
        }
        .filter-chip button { width: 18px; height: 18px; border-radius: 50%; display: grid; place-content: center; color: var(--brand-500); border: 0; background: transparent; cursor: pointer; }
        .filter-chip button:hover { background: var(--brand-100); color: var(--brand-700); }
      `}</style>
    </span>
  );
}

function synthesiseColumnOrder(visibleIds: string[], columns: { id: string }[]): string[] {
  const seen = new Set(visibleIds);
  const tail = columns.map((c) => c.id).filter((id) => !seen.has(id));
  return [...visibleIds, ...tail];
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  if (typeof a !== typeof b) return false;
  if (typeof a !== "object") return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) {
    const bs = b as unknown[];
    if (a.length !== bs.length) return false;
    return a.every((x, i) => deepEqual(x, bs[i]));
  }
  const ak = Object.keys(a as object);
  const bk = Object.keys(b as object);
  if (ak.length !== bk.length) return false;
  return ak.every((k) => deepEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
}
